import React, { useContext, useCallback, useEffect } from 'react';
import { CAContext } from './src-contexts-CAContext.js';
import { ChatbotContext } from './src-contexts-ChatbotContext.js';
import { util } from './src-utils-miaw-common.js';
import { DISPLAY_NAME } from './src-config-app-config.js';
import ButtonGroup from './src-components-AdvanceMessagingComponent-ButtonGroup-ButtonGroup.js';
import PrechatForm from './src-components-PrechatFormInline-PrechatFormInline.js';
import { MiawChatContext } from './src-contexts-MiawChatContext.js';
import { useIdentity } from '@vp/auth-react';
import { useNRLogging } from './src-hooks-custom-hooks-useNRLogging.js';

function SummaryFlowLogic({ formData, setAutoFilled, setFormData, correctSummary }) {
  const identity = useIdentity();
  const { logCustomAttributesNR } = useNRLogging();
  const {
    chatState: { userInfo, chatbotSummaryResponse },
    setChatState
  } = useContext(CAContext);
  const {
    state: { startMiawChatSession },
    setState: setMiawChatState
  } = useContext(MiawChatContext);
  const {
    state: { sessionId },
    setState: setChatbotState
  } = useContext(ChatbotContext);
  const handleSummaryGenerated = () => {
    const summaryMessageId = util.generateUUID();
    const timestamp = Date.now();
    setChatbotState((prevState) => ({
      ...prevState,
      conversationMessages: [
        ...prevState.conversationMessages,
        {
          id: summaryMessageId,
          timestamp,
          type: "chatbot",
          messageContent: `Last thing! Please review the summary below. I\u2019ll pass it to the specialist so you won\u2019t need to repeat yourself:


${formData.chatbotContactReason}`,
          additionalComponents: /* @__PURE__ */ React.createElement(
            ButtonGroup,
            {
              type: "buttons",
              actionList: [
                { label: "Yes", value: "Yes", isPreChatFlowButton: true },
                { label: "No", value: "No", isPreChatFlowButton: true }
              ],
              onSelect: (value) => handleOnSelectSummaryCheck(value, summaryMessageId)
            }
          ),
          isMessageRead: false,
          showFeedback: false
        }
      ]
    }));
  };
  function handleOnSelectSummaryCheck(value, summaryMessageId) {
    if (value === "No") {
      logCustomAttributesNR("ca-chatbot-info", {
        logName: "escalation_flow_user_clicked_no",
        logLevel: "info",
        message: "User clicked on No button and edited the summary"
      });
      setChatbotState((prev) => ({
        ...prev,
        conversationMessages: removeButtons(prev.conversationMessages, summaryMessageId)
      }));
      setChatbotState((prev) => ({
        ...prev,
        conversationMessages: removeButtons(prev.conversationMessages, summaryMessageId)
      }));
      setChatbotState((prevState) => ({
        ...prevState,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            id: util.generateUUID(),
            messageContent: "No",
            timestamp: (/* @__PURE__ */ new Date()).getTime(),
            type: "user",
            isMessageRead: false,
            showFeedback: false
          }
        ]
      }));
      setChatbotState((prevState) => ({
        ...prevState,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            id: util.generateUUID(),
            messageContent: "Can you please type in the correction needed?",
            timestamp: (/* @__PURE__ */ new Date()).getTime(),
            type: "chatbot",
            isMessageRead: false,
            senderDisplayName: DISPLAY_NAME.CHATBOT,
            additionalComponents: /* @__PURE__ */ React.createElement(PrechatForm, { onSubmit: handleSummaryCorrectedByUser, correctSummary: true }),
            customMessageType: "prechatMessage",
            showFeedback: false
          }
        ]
      }));
    } else {
      logCustomAttributesNR("ca-chatbot-info", {
        logName: "escalation_flow_user_clicked_yes",
        logLevel: "info",
        message: "User clicked on Yes button and accepted the generated response"
      });
      setChatbotState((prev) => ({
        ...prev,
        conversationMessages: removeButtons(prev.conversationMessages, summaryMessageId)
      }));
      setMiawChatState((prevState) => ({
        ...prevState,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            id: util.generateUUID(),
            messageContent: "Yes",
            timestamp: (/* @__PURE__ */ new Date()).getTime(),
            type: "user",
            isMessageRead: false,
            showFeedback: false
          }
        ]
      }));
      startMiawChatSession?.({
        email: userInfo?.isLoggedIn && userInfo?.userData ? userInfo?.userData.email : formData.email || "",
        firstName: userInfo?.isLoggedIn && userInfo?.userData ? userInfo?.userData.firstName : formData.firstName || "",
        lastName: userInfo?.isLoggedIn && userInfo?.userData ? userInfo?.userData.lastName : formData.lastName || "",
        chatbotIdentifier: sessionId || "",
        chatbotContactReason: formData.chatbotContactReason || "",
        orderNumber: userInfo?.isLoggedIn && userInfo?.userData ? userInfo?.userData?.orderNumber ? userInfo?.userData?.orderNumber : formData.orderNumber : formData.orderNumber
      });
    }
  }
  const handleSummaryCorrectedByUser = useCallback(
    (data) => {
      setChatState((prevState) => ({
        ...prevState,
        userInfo: {
          isLoggedIn: prevState.userInfo?.isLoggedIn ?? false,
          userData: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            shopperId: identity.canonicalId || "",
            chatbotContactReason: data?.chatbotContactReason || "",
            orderNumber: data?.orderNumber || ""
          }
        }
      }));
      setChatbotState((prevState) => ({
        ...prevState,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            id: util.generateUUID(),
            messageContent: data.chatbotContactReason,
            timestamp: (/* @__PURE__ */ new Date()).getTime(),
            type: "user",
            isMessageRead: false,
            showFeedback: false
          }
        ]
      }));
      startMiawChatSession?.({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        chatbotIdentifier: sessionId || "",
        chatbotContactReason: data?.chatbotContactReason || ""
      });
      setChatbotState((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.map((message) => {
          if (message.customMessageType === "prechatMessage") {
            return { ...message, additionalComponents: void 0 };
          }
          return message;
        })
      }));
    },
    [sessionId, startMiawChatSession, setChatState, setChatbotState, identity]
  );
  const removeButtons = (messages, summaryMessageId) => messages.map((msg) => msg.id === summaryMessageId ? { ...msg, additionalComponents: void 0 } : msg);
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = chatbotSummaryResponse;
        const inquiry = response?.summary == "NA" || response?.summary == "" ? "" : response?.summary;
        const isValidInquiry = !!inquiry;
        setAutoFilled(isValidInquiry);
        setFormData((prev) => ({
          ...prev,
          isAPICalled: true,
          chatbotContactReason: isValidInquiry ? inquiry : "",
          orderNumber: response?.order_id === "" || response?.order_id === "NA" ? "" : response?.order_id
        }));
        setChatbotState((prev) => ({
          ...prev,
          orderNumber: response?.order_id === "" || response?.order_id === "NA" ? "" : response?.order_id
        }));
      } catch {
        setFormData((prev) => ({ ...prev, isAPICalled: true }));
        setChatbotState((prev) => ({
          ...prev,
          isSendMessageLoading: false
        }));
      }
    };
    if (userInfo && userInfo.userData && correctSummary) {
      const chatbotContactReasonString = `${userInfo?.userData?.chatbotContactReason}`;
      setFormData((prev) => ({
        ...prev,
        isAPICalled: true,
        firstName: userInfo?.userData?.firstName,
        lastName: userInfo?.userData?.lastName,
        email: userInfo?.userData?.email,
        chatbotContactReason: chatbotContactReasonString
      }));
    } else {
      fetchSummary();
    }
  }, []);
  return {
    handleSummaryGenerated
  };
}

export { SummaryFlowLogic as default };
//# sourceMappingURL=src-components-PrechatFormInline-useSummaryFlowLogic.js.map
